"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
exports.__esModule = true;
exports.default = void 0;
var _extends2 = _interopRequireDefault(require("@babel/runtime/helpers/extends"));
var _objectWithoutPropertiesLoose2 = _interopRequireDefault(require("@babel/runtime/helpers/objectWithoutPropertiesLoose"));
var _react = _interopRequireDefault(require("react"));
var _classnames = _interopRequireDefault(require("classnames"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _styles = _interopRequireDefault(require("../styles"));
var _Accordion = _interopRequireDefault(require("@mui/material/Accordion"));
var _ExpansionPanelContext = _interopRequireDefault(require("./internal/ExpansionPanelContext"));
var _globalSpacing = require("./../globalSpacing");
var _tokens = require("../styles/tokens");
var _excluded = ["classes", "panelSpacingVariant", "variant", "spacingOptions", "TransitionProps"];
var styles = function styles(theme) {
  var _$expanded$separate, _separated;
  return {
    root: {
      '&$expanded$nogap': {
        marginTop: 0,
        marginBottom: 0,
        '&:before': {
          opacity: 1
        }
      },
      '&$expanded$nogap + &': {
        '&:before': {
          display: 'block'
        }
      },
      '&$expanded&$separated': (_$expanded$separate = {
        margin: _tokens.spacing40 + " 0"
      }, _$expanded$separate[theme.breakpoints.up('md')] = {
        margin: _tokens.spacing50 + " 0"
      }, _$expanded$separate),
      '&$disabled': {
        backgroundColor: theme.palette.grey[100]
      },
      backgroundColor: theme.palette.grey[100]
    },
    card: {
      boxShadow: "\n            0 2px 1px -1px rgba(0, 0, 0, 0.12),\n            0 1px 1px 0 rgba(0, 0, 0, 0.14),\n            0 1px 3px 0 rgba(0, 0, 0, 0.21)\n        "
    },
    default: {
      boxShadow: "\n            0 19px 20px 0 rgba(0, 0, 0, 0.03),\n            0 3px 8px 0 rgba(0, 0, 0, 0.06)\n        "
    },
    disabled: {},
    expanded: {},
    separated: (_separated = {
      margin: _tokens.spacing40 + " 0"
    }, _separated[theme.breakpoints.up('md')] = {
      margin: _tokens.spacing50 + " 0"
    }, _separated['& + &'] = {
      '&:before': {
        opacity: 0
      }
    }, _separated),
    nogap: {},
    none: {},
    region: {}
  };
};

/**
 * Use `ExpansionPanel` to organize content into expandable and collapsible sections.
 *
 * @done true
 * @updated v7.1.0
 * @versionAdded v0.0.13
 * @examples
 *  ExpansionPanelSimple
 *  ExpansionPanelCard
 *  ExpansionPanelControlledAccordion
 *  ExpansionPanelLeftIcon
 *  ExpansionPanelSecondary
 */
var ExpansionPanel = /*#__PURE__*/_react.default.forwardRef(function (props, ref) {
  var _classNames, _classNames2, _classNames3, _classNames4, _classNames5;
  var classes = props.classes,
    panelSpacingVariant = props.panelSpacingVariant,
    variant = props.variant,
    spacingOptions = props.spacingOptions,
    TransitionProps = props.TransitionProps,
    rest = (0, _objectWithoutPropertiesLoose2.default)(props, _excluded);
  var spacingOptionsLocal = (0, _globalSpacing.getSpacingOptions)(spacingOptions);
  var classOverrides = {};
  classOverrides.root = (0, _classnames.default)(classes.root, (_classNames = {}, _classNames[classes.nogap] = panelSpacingVariant !== 'separated', _classNames), (_classNames2 = {}, _classNames2[classes.separated] = panelSpacingVariant === 'separated', _classNames2), (_classNames3 = {}, _classNames3[classes.card] = variant === 'card', _classNames3), (_classNames4 = {}, _classNames4[classes.default] = variant === 'default', _classNames4), (_classNames5 = {}, _classNames5[classes.none] = spacingOptionsLocal.spacing === _globalSpacing.spacingVariant.NONE, _classNames5));
  classOverrides.expanded = classes.expanded;
  classOverrides.disabled = classes.disabled;
  classOverrides.region = classes.region;
  return /*#__PURE__*/_react.default.createElement(_ExpansionPanelContext.default.Provider, {
    value: {
      spacingOptions: spacingOptionsLocal,
      disabled: props.disabled
    }
  }, /*#__PURE__*/_react.default.createElement(_Accordion.default, (0, _extends2.default)({
    disabled: props.disabled,
    TransitionProps: TransitionProps
  }, rest, {
    classes: classOverrides,
    ref: ref
  })));
});
ExpansionPanel.muiName = 'Accordion';
ExpansionPanel.displayName = 'ExpansionPanel';
ExpansionPanel.propTypes = {
  /**
   * The content of the expansion panel.
   */
  children: _propTypes.default.node.isRequired,
  /**
   * Override or extend the styles applied to the component.
   */
  classes: _propTypes.default.object,
  /**
   * @ignore
   */
  className: _propTypes.default.string,
  /**
   * If `true`, expands the panel by default.
   */
  defaultExpanded: _propTypes.default.bool,
  /**
   * If `true`, the panel will be displayed in a disabled state.
   */
  disabled: _propTypes.default.bool,
  /**
   * If `true`, expands the panel, otherwise collapse it.
   * Setting this prop enables control over the panel.
   */
  expanded: _propTypes.default.bool,
  /**
   * Callback fired when the expand/collapse state is changed.
   *
   * @param {React.SyntheticEvent} event The event source of the callback. **Warning**: This is a generic event not a change event.
   * @param {boolean} expanded The `expanded` state of the accordion.
   */
  onChange: _propTypes.default.func,
  /**
   * The system prop that allows defining system overrides as well as additional CSS styles.
   */
  sx: _propTypes.default.oneOfType([_propTypes.default.arrayOf(_propTypes.default.oneOfType([_propTypes.default.func, _propTypes.default.object, _propTypes.default.bool])), _propTypes.default.func, _propTypes.default.object]),
  /**
   * Determines the spacing between panels in a group of expansion panels.
   *
   *   * none - No separation between panels (the default)
   *   * separated - Each panel is separated from its siblings
   */
  panelSpacingVariant: _propTypes.default.oneOf(['none', 'separated']),
  /**
   * Styles the ExpansionPanel for the context it is on, either 'default' or 'card'.
   */
  variant: _propTypes.default.oneOf(['default', 'card']),
  /**
   * Override global spacing options.
   * Available options are:
   * * `spacing: 'standard' | 'none'`
   *      * Controls the padding around the `ExpansionPanel` as a whole, and between the elements of the expansion panel
   * * `responsive: true | false`
   *      * Adjusts spacing automatically on desktop, tablet and mobile viewports
   * * `outerSpacing: true | false`
   *      * Removes the padding around the `ExpansionPanel`
   */
  spacingOptions: _propTypes.default.shape({
    spacing: _propTypes.default.string,
    responsive: _propTypes.default.bool,
    outerSpacing: _propTypes.default.bool
  }),
  /**
   * The component used for the transition.
   */
  TransitionComponent: _propTypes.default.elementType,
  /**
   * Props applied to the transition element.
   */
  TransitionProps: _propTypes.default.object
};

// If default props are needed, they must be set here
ExpansionPanel.defaultProps = {
  variant: 'default',
  panelSpacingVariant: 'none',
  defaultExpanded: false,
  disabled: false
};
var _default = exports.default = (0, _styles.default)(styles)(ExpansionPanel);